<script setup lang="ts">
/**
 * UButton — 造物工厂基件按钮 (SPEC_UI_UX_UPGRADE T1-3)
 * 统一 852 处裸 <button> 的视觉与交互范式：
 * - 宋韵视觉: 0.5px 乌丝栏边框、朱砂 danger、token 化配色（双主题安全）
 * - 内建 loading / disabled / focus-visible 无障碍基线
 * - variant: primary | secondary | ghost | danger
 * - size: sm | md
 */
import { computed } from 'vue';

const props = withDefaults(defineProps<{
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  loading?: boolean;
  disabled?: boolean;
  block?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  ariaLabel?: string;
}>(), {
  variant: 'secondary',
  size: 'md',
  loading: false,
  disabled: false,
  block: false,
  type: 'button',
});

const isDisabled = computed(() => props.disabled || props.loading);

const variantClass = computed(() => {
  switch (props.variant) {
    case 'primary':
      return 'bg-[var(--ds-celadon)] text-[var(--color-bg-primary)] border-[0.5px] border-[var(--color-accent)] hover:bg-[var(--color-accent-hover)]';
    case 'danger':
      return 'bg-transparent text-[var(--color-error)] border-[0.5px] border-[rgba(var(--color-error-rgb),0.4)] hover:bg-[rgba(var(--color-error-rgb),0.08)] hover:border-[var(--color-error)]';
    case 'ghost':
      return 'bg-transparent text-[var(--color-text-secondary)] border-[0.5px] border-transparent hover:text-[var(--color-text-primary)] hover:bg-[rgba(var(--color-text-primary-rgb),0.05)]';
    case 'secondary':
    default:
      return 'bg-transparent text-[var(--color-text-primary)] border-[0.5px] border-[var(--color-border)] hover:border-[var(--color-border-hover)] hover:bg-[rgba(var(--color-accent-rgb),0.05)]';
  }
});
</script>

<template>
  <button
    :type="type"
    :disabled="isDisabled"
    :title="title"
    :aria-label="ariaLabel"
    :aria-busy="loading || undefined"
    class="inline-flex items-center justify-center gap-1.5 rounded-[2px] select-none outline-hidden
           transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
           focus-visible:ring-1 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-0
           disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
    :class="[
      variantClass,
      size === 'sm' ? 'h-7 px-2.5 text-[length:var(--ts-caption)]' : 'h-9 px-4 text-[length:var(--ts-label)]',
      block ? 'w-full' : '',
    ]"
  >
    <span
      v-if="loading"
      class="w-[8px] h-[8px] rounded-full border-[1.5px] border-current border-t-transparent opacity-70 animate-spin"
      aria-hidden="true"
    />
    <slot />
  </button>
</template>
